import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useState, но значение переживает F5 и переход между разделами.
 *
 * Понадобилось из-за подинструментов Логистики: после обновления страницы
 * вкладка сбрасывалась на первую (FBS → «Заказы»), а начало периода в
 * «Расчёт FBS ФФ» — на «месяц назад», из-за чего нельзя было зафиксировать
 * дату старта работы с ФФ.
 *
 * `key` должен включать accountId там, где значение зависит от кабинета.
 */
export default function usePersistedState(key, initialValue) {
    const readStored = useCallback(() => {
        if (!key) return undefined;
        try {
            const raw = window.localStorage.getItem(key);
            if (raw === null) return undefined;
            return JSON.parse(raw);
        } catch {
            return undefined;
        }
    }, [key]);

    const [value, setValue] = useState(() => {
        const stored = readStored();
        return stored === undefined ? initialValue : stored;
    });

    // При смене ключа (например, выбрали другой кабинет) перечитываем значение,
    // а не тащим состояние предыдущего кабинета.
    const prevKeyRef = useRef(key);
    useEffect(() => {
        if (prevKeyRef.current === key) return;
        prevKeyRef.current = key;
        const stored = readStored();
        setValue(stored === undefined ? initialValue : stored);
        // initialValue намеренно не в зависимостях: пересчитывать нужно только по key
        // eslint-disable-next-line
    }, [key, readStored]);

    useEffect(() => {
        if (!key) return;
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // localStorage недоступен (приватный режим, квота) — не критично
        }
    }, [key, value]);

    return [value, setValue];
}
